'use client'

import { useEffect, useState } from 'react'
import { Star, Smile, Frown, Send, ArrowRight } from 'lucide-react'

/**
 * Gastbewertungen / Smart-Funnel showcase. Loops between:
 *   0 — Guest rates stars (3 → 4 → 5 — animated)
 *   1 — Branch on rating: 5★ goes to Google review, ≤3★ goes to private
 *       feedback form
 *   2 — Confirmation
 *
 * The "smart funnel" idea: route unhappy guests to a private form (so
 * the owner can fix it before it becomes a 1-star Google review).
 */
type Path = 'google' | 'private'

export function AnimatedReviewsFlow() {
  const [step, setStep] = useState(0)
  const [stars, setStars] = useState(0)
  const [path, setPath] = useState<Path>('google')
  const [paused, setPaused] = useState(false)

  // Cycle: 0 (animate stars 3→4→5) → 1 (branch) → 2 (confirm) → 0
  useEffect(() => {
    if (paused) return
    const ids: ReturnType<typeof setTimeout>[] = []
    if (step === 0) {
      // Toggle between routes each cycle so visitors see both
      setStars(0)
      ids.push(setTimeout(() => setStars(3), 600))
      ids.push(setTimeout(() => setStars(4), 1400))
      ids.push(setTimeout(() => {
        // Alternate paths each cycle
        const nextPath = Math.random() > 0.5 ? 'google' : 'private'
        setPath(nextPath)
        setStars(nextPath === 'google' ? 5 : 2)
      }, 2200))
      ids.push(setTimeout(() => setStep(1), 3200))
    } else if (step === 1) {
      ids.push(setTimeout(() => setStep(2), 3000))
    } else {
      ids.push(setTimeout(() => setStep(0), 2800))
    }
    return () => ids.forEach(clearTimeout)
  }, [step, paused])

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative rounded-2xl border border-pub-rule bg-pub-surface overflow-hidden"
      style={{ boxShadow: '0 20px 60px -20px rgba(23,106,71,0.18)' }}>

      <div className="flex items-center justify-between border-b border-pub-rule px-5 py-3 bg-pub-bg">
        <div className="flex items-center gap-2">
          <Star className="h-3.5 w-3.5 text-brand-700" />
          <p className="text-[12px] font-semibold text-pub-ink">Bewertungen · Smart-Funnel</p>
        </div>
        <div className="flex items-center gap-1">
          {[0, 1, 2].map(i => (
            <span key={i} className={`h-1 w-6 rounded-full transition-colors ${step === i ? 'bg-brand-600' : 'bg-pub-rule'}`} />
          ))}
        </div>
      </div>

      <div className="relative" style={{ height: 380 }}>
        {/* STEP 0 — star rating */}
        <Pane visible={step === 0}>
          <div className="p-5 flex flex-col items-center pt-8">
            <p className="text-[11px] uppercase tracking-widest text-pub-muted mb-3">Wie war Ihr Besuch?</p>
            <h3 className="text-2xl text-pub-ink leading-tight mb-6" style={{ fontFamily: 'var(--font-display)' }}>
              Restaurant Verde
            </h3>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <Star key={n}
                  className={`h-9 w-9 transition-all duration-300 ${
                    stars >= n ? 'fill-amber-400 text-amber-400 scale-110' : 'text-pub-rule'
                  }`}
                  strokeWidth={1.5}
                />
              ))}
            </div>
            <p className="mt-6 text-[12px] text-pub-muted h-4">
              {stars === 0 && ''}
              {stars >= 1 && stars <= 2 && 'Vielen Dank für Ihr Feedback'}
              {stars === 3 && 'Was können wir besser machen?'}
              {stars === 4 && 'Schön, dass es Ihnen gefallen hat!'}
              {stars === 5 && 'Hervorragend!'}
            </p>
          </div>
        </Pane>

        {/* STEP 1 — branch */}
        <Pane visible={step === 1}>
          {path === 'google' ? (
            <div className="p-5 flex flex-col items-center pt-6">
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map(n => (
                  <Star key={n} className="h-5 w-5 fill-amber-400 text-amber-400" strokeWidth={1.5} />
                ))}
              </div>
              <p className="text-[13px] text-pub-muted mb-4 text-center max-w-sm">
                Würden Sie das auch anderen verraten? Eine Google-Bewertung würde uns sehr helfen.
              </p>
              <a className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-5 py-2.5 text-[13px] font-semibold text-white"
                style={{ animation: 'rzr-pop 0.5s ease-out both' }}>
                Auf Google bewerten
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
              <p className="mt-3 text-[10.5px] text-pub-muted">Direkt-Link zum Google-Profil</p>
            </div>
          ) : (
            <div className="p-5 flex flex-col items-center pt-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 border border-amber-200 mb-3">
                <Frown className="h-6 w-6 text-amber-600" />
              </div>
              <p className="text-[13px] text-pub-ink font-medium mb-1">Das tut uns leid.</p>
              <p className="text-[12px] text-pub-muted mb-4 text-center max-w-sm">
                Schreiben Sie uns kurz — der Inhaber meldet sich persönlich. Niemand außer uns sieht diese Nachricht.
              </p>
              <div className="w-full max-w-sm rounded-md border border-pub-rule bg-white p-3 mb-3">
                <p className="text-[11.5px] text-pub-ink">
                  Service war heute langsam, der Tisch hatte 20 Min. gewartet…
                  <span className="ml-px inline-block w-px h-3 bg-brand-600 animate-pulse align-middle" />
                </p>
              </div>
              <a className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-5 py-2 text-[12px] font-semibold text-white">
                <Send className="h-3.5 w-3.5" /> Privat an den Inhaber
              </a>
            </div>
          )}
        </Pane>

        {/* STEP 2 — confirmation */}
        <Pane visible={step === 2}>
          <div className="p-5 flex flex-col items-center pt-12">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-100 mb-4"
              style={{ animation: 'rzr-pop 0.5s ease-out both' }}>
              <Smile className="h-7 w-7 text-brand-700" strokeWidth={2} />
            </div>
            <h3 className="text-xl text-pub-ink leading-tight text-center" style={{ fontFamily: 'var(--font-display)' }}>
              Danke für Ihr Feedback!
            </h3>
            <p className="mt-2 text-[12px] text-pub-muted text-center max-w-xs">
              {path === 'google'
                ? 'Wir sehen Ihre Bewertung in Echtzeit im Dashboard.'
                : 'Wir melden uns innerhalb von 24 Stunden persönlich.'}
            </p>
          </div>
        </Pane>
      </div>

      <style>{`
        @keyframes rzr-pop { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  )
}

function Pane({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  return (
    <div
      aria-hidden={!visible}
      className="absolute inset-0 transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        pointerEvents: visible ? 'auto' : 'none',
      }}>
      {children}
    </div>
  )
}
